import { useEffect, useState } from "react";
import Notification from "../../../../components/toast";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, defaultValues } from "./schema";
import { format, parseISO } from "date-fns";
import LibBoolean from "../../../../util/libBool";

export const IndexHook = ({
	isOpenModalRegister,
	ident,
	idTable,
	selectedData,
	setSelectData,
	setLoading: setParentLoading,
	fetchID,
	reloadData,
	insert,
}) => {
	const {
		register,
		handleSubmit,
		setValue,
		setFocus,
		reset,
		control,
		formState: { errors, isSubmitting },
	} = useForm({
		mode: "onSubmit",
		resolver: zodResolver(schema),
		defaultValues: {
            ...defaultValues,
            tipo_desconto: "PERCENTUAL"
        },
	});

	const [loading, setLoading] = useState(false);
    
    // Observa o tipo de desconto para alternar os campos
    const tipoDesconto = useWatch({
        control,
        name: "tipo_desconto",
        defaultValue: "PERCENTUAL"
    });

	const handleCloseModalRegister = () => {
		const event = new CustomEvent(`${ident}`);
		window.dispatchEvent(event);
	};

	//#region Fetch
	useEffect(() => {
		if (isOpenModalRegister) {
			const id =
				typeof selectedData[idTable] === "number" && !isNaN(selectedData[idTable])
					? selectedData[idTable]
					: 0;

			if (id > 0) {
				fetch(id);
			}
		} else {
			if (setSelectData) setSelectData({});
			reset();
		}
	}, [isOpenModalRegister, selectedData]);

	const fetch = async (id) => {
		try {
			setLoading(true);

			const response = await fetchID(id);

			if (response.data && typeof response.data === "object") {
				const data = Array.isArray(response.data) ? response.data[0] : response.data;
				
				const newData = await processNewData(data);

				Object.keys(newData).forEach((field) => {
					setValue(field, newData[field]);
				});
			}
		} catch (error) {
			Notification.showToast({
				message: error.message,
				type: "warning",
			});
		} finally {
			setLoading(false);
		}
	};

	const processNewData = async (data) => {
		const formatDateForInput = (dateStr) => {
			if (!dateStr) return "";
			try {
				const date = parseISO(dateStr);
				return format(date, "yyyy-MM-dd");
			} catch (e) {
				return dateStr;
			}
		};

        const perc = Number(data.perc_desconto || data.PERC_DESCONTO || 0);

		return {
			...data,
			inicio: formatDateForInput(data.inicio || data.INICIO),
			fim: formatDateForInput(data.fim || data.FIM),
			todos_produtos: LibBoolean.strToBoolean(data.todos_produtos || data.TODOS_PRODUTOS),
            status: (data.status || data.STATUS) === "ATIVO", // Converte string para boolean para o check
            tipo_desconto: perc > 0 ? "PERCENTUAL" : "VALOR",
		};
	};
	//#endregion

	const getReturnUpdated = (response) => {
		if (Array.isArray(response.data)) {
			return response.data[0];
		} else if (typeof response.data === "object") {
			return response.data;
		} else {
			throw new Error("Formato do retorno inválido.");
		}
	};

	//#region onSubmit
	const handleOnSubmit = async (data) => {
		try {
			const newData = ValidateSubmit(data);

			const oldID = data[idTable];

			const response = await insert(newData);

			const returnUpdated = getReturnUpdated(response);
			const currentId = returnUpdated[idTable];

			await reloadData(oldID, currentId);

			Notification.showToast({
				message: "Registro salvo com sucesso.",
				type: "success",
			});
			reset();
			handleCloseModalRegister();
		} catch (error) {
			Notification.showToast({
				message: error.message,
				type: "error",
			});
		}
	};

	const ValidateSubmit = (data) => {
		const finalData = {
			...data,
			todos_produtos: LibBoolean.booleanToStr(data.todos_produtos),
            status: data.status ? "ATIVO" : "INATIVO", // Converte boolean de volta para string
		};

        // Zera o campo não utilizado
        if (data.tipo_desconto === "PERCENTUAL") {
            finalData.valor_desconto = 0;
        } else {
            finalData.perc_desconto = 0;
        }

        return finalData;
	};
	//#endregion

	return {
		//hooks
		handleOnSubmit,
		handleCloseModalRegister,

		//useForm
		register,
		handleSubmit,
		setValue,
		control,
		setFocus,

		loading,
        tipoDesconto,

		//formState
		errors,
		isSubmitting,
	};
};
